/**
 * Real task dispatch form that submits `DispatchTaskRequest`-shaped
 * payloads to `POST /tasks/dispatch_task`.
 *
 * The form varies by category and shows ONLY the fields each task
 * actually needs:
 *   - station → destination (one input)
 *   - patrol  → waypoints (comma-separated, one input)
 *   - clean   → zone (one select). Offered as one choice PER MODE the
 *               site has — Vacuum, Sweep, Dust-mop, Scrub — each dispatching
 *               category `clean` with `params.mode` (see `clean-modes.ts`)
 *   - dustbag → station (one select over the catalog's declared list),
 *               plus an OPTIONAL wait in minutes (empty = the site's)
 *   - charge  → charger place (one select over the catalog's declared list)
 *
 * A collapsible "Advanced" section (default closed) holds:
 *   - Priority: Normal | High (2026-10-31). Only a supervisor may choose
 *     High; the choice is locked, with the reason, for anyone else
 *   - Notes
 *   - "Schedule for later" toggle → reveals Start time
 *
 * The Duration field has been removed; the upstream RMF scheduler
 * derives a duration from the phase waypoints. There is deliberately
 * no Finish time field either: the v1 `TaskRequest` contract
 * (`backend/v1_contract/v1_contract/schemas.py`) carries only
 * `earliest_start_ms` — no deadline/finish field exists to send one
 * to. An earlier version of this form collected, validated, and
 * displayed a "Finish time" that `submit()` silently dropped on the
 * floor; removed rather than wired up, since adding a real deadline
 * is a contract change, not a form change.
 *
 * The form can be mounted in two modes:
 *   - inline (top of the Tasks screen)
 *   - modal (inside a TaskModal, opened by clicking an empty
 *     calendar cell or the "+ New task" button). `prefillStart`
 *     (optional) seeds the start time when the "Schedule for later"
 *     toggle is on.
 */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FireAlarmContext } from '../../state/fire-alarm.context';
import { useActionsOff } from '../../auth/useRolePreview';
import { useDispatchTaskV1 } from '../../api/hooks/useTasks';
import { useCreateFavoriteV1 } from '../../api/hooks/useFavorites';
import {
  FIRE_MODE_HOLD_COPY,
  isFireModeError,
  useCreateSchedule,
} from '../../api/hooks/useSchedules';
import {
  MAINTENANCE_HOLD_COPY,
  isMaintenanceHold,
  useMaintenanceWindows,
  windowCovering,
} from '../../api/hooks/useMaintenance';
import { usePlaces } from '../../api/hooks/usePlaces';
import { useActiveSite } from '../../state/useActiveSite';
import { WaypointMultiSelect } from './WaypointMultiSelect';
import { RecurrenceEditor } from './RecurrenceEditor';
import {
  ONE_SHOT_RRULE,
  buildRRule,
  buildUntilMs,
  defaultRecurrenceSpec,
  dtstartMsInZone,
  type RecurrenceSpec,
} from './rrule';
import { resolveSiteTimeZone, siteTimeZoneWarning } from './site-timezone';
import { formatZonedDateTime, timeZoneLabel, zonedParts, zonedWeekday } from './zoned-time';
import { DateTimeField } from '../../design-system/components/DateField';
import { useTaskCatalog } from '../../api/hooks/useTaskCatalog';
import {
  CLEAN_MODE_LABEL,
  DEFAULT_CLEAN_MODE,
  offeredCleanModes,
  ROUTINE_CHECK_LABEL,
  type CleanMode,
} from './clean-modes';
import {
  DEFAULT_CATEGORY_FORM_VALUES,
  DUSTBAG_WAIT_MAX_MINUTES,
  DUSTBAG_WAIT_MIN_MINUTES,
  cleanPickerValue,
  dispatchFieldsFromForm,
  fromV1Category,
  parseDustbagWaitMinutes,
  parsePickerValue,
  type Category,
  type CategoryFormValues,
  type DispatchFields,
} from './task-request-form';
import { PriorityChoice } from './TaskPriority';
import { priorityEffects, useCanSetHighPriority } from './task-priority';
import { isHighPriority, isHighPriorityRefusal, priorityLevel } from '../../api/task-priority';
import './TaskDispatchForm.css';

// The form's category vocabulary lives with the rest of its task
// helpers now (`task-request-form.ts`); re-exported so a parent can
// still type a `prefillCategory` prop from here.
export type { Category } from './task-request-form';

const PLACES_DATALIST_ID = 'nx-task-form-places';

/**
 * How close to its start time a one-shot has to be before the calendar
 * hands it straight to the fleet instead of holding it.
 *
 * It is the server's own `GRACE_MS` (`backend/api_server/scheduler.py`),
 * and it has to be, in this direction: the scheduler dispatches an
 * occurrence up to `GRACE_MS` late and records anything older as
 * `missed` WITHOUT dispatching it. So a start time already past -- or
 * one that will be past by the time the ~20 s tick comes round -- turns
 * a held task into a task that silently never runs. Below this
 * threshold RMF's own `earliest_start_ms` is both correct and better:
 * it starts the task at the requested instant rather than at the next
 * tick after it.
 *
 * Above it, holding is what the operator asked for: a task booked for
 * Thursday should not sit in a robot's queue from Monday.
 */
const NEAR_TERM_MS = 5 * 60 * 1000;

/**
 * The bucket the maintenance lookup range is quantised to.
 *
 * The range is part of the query key, so the obvious range —
 * `[at - 1h, at + 1h]` — would be a new key, and therefore a new
 * request, for every digit typed into the start-time field. A day
 * bucket moves only when the operator moves to another date.
 *
 * Over-fetching costs nothing here: `fetchMaintenanceWindows` returns
 * windows OVERLAPPING the range, so a three-day range still finds the
 * week-long window covering the instant, and a site has a handful of
 * rows.
 */
const MAINTENANCE_BUCKET_MS = 24 * 60 * 60 * 1000;

/**
 * How often the open schedule section re-reads the clock.
 *
 * The only thing that depends on it is whether a quick-start chip has
 * gone stale ("Tonight 22:00" at 22:01), and the only reason a timer is
 * needed at all is that a form can sit open with nothing else causing a
 * render. Half a minute is well under the resolution anyone can notice
 * on a chip that names a whole-hour moment, and it costs one setState
 * per tick — the chips' VALUES stay memoised, so nothing rewrites
 * itself under the cursor (see `quickPickTargets`).
 */
const SCHEDULE_CLOCK_TICK_MS = 30 * 1000;

/** What an empty wait field means when the catalog does not say: the
 *  worker's platform default (`_DUSTBAG_DEFAULT_TIMEOUT_S`, 1800 s).
 *  A current worker and api-server always say, so silence means one of
 *  them predates the field. The only time this number can then be
 *  wrong is a site that set a different wait while one of the two is
 *  still old, and that ends when both are current. */
const DUSTBAG_PLATFORM_DEFAULT_MINUTES = 30;

/** The site's default dustbag wait in minutes, from the catalog's
 *  seconds. The deploy repo renders whole minutes, so the rounding only
 *  matters for a hand-set variable. */
function siteDustbagWaitMinutes(timeoutS: number | null | undefined): number {
  if (typeof timeoutS !== 'number' || !Number.isFinite(timeoutS) || timeoutS <= 0) {
    return DUSTBAG_PLATFORM_DEFAULT_MINUTES;
  }
  return Math.max(1, Math.round(timeoutS / 60));
}

/** ` (lift service)`, or nothing at all. The reason is what the operator
 *  who booked the window typed, and the only thing that says WHICH
 *  window is holding this dispatch. */
function maintenanceWindowName(reason: string | null | undefined): string {
  const trimmed = reason?.trim();
  return trimmed ? ` (${trimmed})` : '';
}

/** A stable fetch range around `atMs`: the day it falls in, padded a day
 *  either side. Quantised — see `MAINTENANCE_BUCKET_MS`. */
function maintenanceLookupRange(atMs: number): { fromMs: number; toMs: number } {
  const dayStart = Math.floor(atMs / MAINTENANCE_BUCKET_MS) * MAINTENANCE_BUCKET_MS;
  return {
    fromMs: dayStart - MAINTENANCE_BUCKET_MS,
    toMs: dayStart + 2 * MAINTENANCE_BUCKET_MS,
  };
}

interface FieldError {
  field: string;
  message: string;
}

interface DispatchError extends Error {
  status?: number;
  body?: unknown;
}

function extractPydanticErrors(body: unknown): {
  fieldErrors: FieldError[];
  formError: string | null;
} {
  if (!body || typeof body !== 'object') return { fieldErrors: [], formError: null };
  const b = body as { detail?: unknown };
  const detail = b.detail;
  if (typeof detail === 'string') return { fieldErrors: [], formError: detail };
  if (!Array.isArray(detail)) return { fieldErrors: [], formError: null };
  const fieldErrors: FieldError[] = [];
  for (const item of detail) {
    if (!item || typeof item !== 'object') continue;
    const entry = item as { loc?: unknown; msg?: unknown; type?: unknown };
    const loc = Array.isArray(entry.loc) ? entry.loc : [];
    const field = loc.length >= 2 ? String(loc[loc.length - 1]) : '';
    const msg = typeof entry.msg === 'string' ? entry.msg : 'Invalid value';
    if (field) fieldErrors.push({ field, message: msg });
  }
  return { fieldErrors, formError: null };
}

function localIsoForInput(d: Date): string {
  // datetime-local needs `YYYY-MM-DDTHH:mm` in local time.
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const pad2 = (n: number) => n.toString().padStart(2, '0');

/**
 * A `datetime-local` value for `hour`:00 on the day `daysAhead` days
 * after today — counted on the SITE's calendar, not the browser's.
 *
 * The field's digits are a wall clock that `dtstartMsInZone` later reads
 * on the site's clock, so "tomorrow 09:00" has to be tomorrow *there*.
 * For a London operator booking a Singapore hotel after 16:00 those are
 * two different days, and the browser's answer is the wrong one.
 *
 * The date shift goes through `Date.UTC` so it is pure calendar
 * arithmetic — no zone, no DST, no hour-of-day involved. Month rollover
 * is `Date.UTC`'s own (day 32 of January is 1 February).
 */
function quickPickValue(
  timeZone: string,
  daysAhead: number,
  hour: number,
  nowMs: number,
): string {
  const today = zonedParts(nowMs, timeZone);
  const shifted = new Date(Date.UTC(today.year, today.month - 1, today.day + daysAhead));
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(
    shifted.getUTCDate(),
  )}T${pad2(hour)}:00`;
}

/** Days from today to the next Monday on `timeZone`'s calendar. Never
 *  0: asked on a Monday, "next Monday" is the one a week away, not the
 *  morning that is already half over. */
function daysToNextMonday(timeZone: string, nowMs: number): number {
  const weekday = zonedWeekday(nowMs, timeZone); // 0 = Sunday
  const delta = (1 - weekday + 7) % 7;
  return delta === 0 ? 7 : delta;
}

export function TaskDispatchForm({
  onSuccess,
  prefillStart,
  prefillCategory,
  prefillZone,
}: {
  onSuccess?: () => void;
  prefillStart?: Date;
  /** Open the form on this category (the Overview map's click-a-zone →
   *  Clean flow). Seeds the initial selection AND is re-asserted once
   *  when the catalog arrives — see the prefill effect below for why
   *  both are needed. The form is self-sufficient: a parent may mount
   *  it before the catalog query has resolved. */
  prefillCategory?: Category;
  /** Preselect a clean zone. Applied ONLY when the catalog's zone list
   *  contains it; an unknown zone leaves the select on its "Select a
   *  zone…" placeholder rather than ever being submitted — an unknown
   *  zone dispatched to rmf_demos' clean executor reports COMPLETED
   *  having cleaned nothing. */
  prefillZone?: string;
}) {
  const { site, sites } = useActiveSite();
  // A schedule belongs to the BUILDING. Every wall clock this form
  // writes — the rule's anchor, its end date, the summary the operator
  // reads back — is interpreted in this zone, never in the browser's.
  const siteZone = useMemo(() => resolveSiteTimeZone(sites, site?.id), [sites, site?.id]);
  const zoneWarning = siteTimeZoneWarning(siteZone);
  const dispatch = useDispatchTaskV1();
  const createScheduleM = useCreateSchedule();
  const placesQuery = usePlaces();
  const catalogQuery = useTaskCatalog();
  // If the registry hasn't loaded yet (or this world genuinely has no
  // named waypoints), don't block submission on it — that would trade
  // "unvalidated garbage accepted" for "nothing can ever be submitted".
  // Once real places ARE known, entries must match one exactly: a
  // free-text place name that doesn't exist on the map can never
  // produce a real RMF route, so letting it through just defers the
  // same failure from this form to an opaque one downstream.
  const knownPlaceNames = useMemo(
    () => new Set((placesQuery.data ?? []).map((p) => p.name)),
    [placesQuery.data],
  );
  const placesLoaded = knownPlaceNames.size > 0;
  // Seeded with the prefill (else VACUUM -- the clean, on its default
  // mode) and then RECONCILED against the catalog below. Vacuum is the
  // default task type (Akash, 2026-10-22): it is what the hotel books
  // most. It cannot be seeded from the catalog because that arrives
  // async, and it must not stay unreconciled: a form sitting on a
  // category the site does not offer submits a request the worker
  // rejects. A site that offers no clean (a patrol-only sim) is moved
  // onto its first offered category by the reconciliation effect.
  const [category, setCategory] = useState<Category>(prefillCategory ?? 'clean');
  /** Which clean, when `category` is `clean`. Its own state beside the
   *  category rather than a field of `values`, for the category's own
   *  reason: a submit resets the fields but leaves the picker where the
   *  operator put it, and a Scrub must not quietly become a Vacuum. A map
   *  zone click opens on the default, Vacuum. */
  const [cleanMode, setCleanMode] = useState<CleanMode>(DEFAULT_CLEAN_MODE);
  const [values, setValues] = useState<CategoryFormValues>(DEFAULT_CATEGORY_FORM_VALUES);
  // "Schedule for later" is off by default — immediate dispatch is the
  // common case. When the user opens the modal from a calendar slot,
  // we pre-turn it on (see the prefill effect below).
  const [scheduled, setScheduled] = useState(false);
  // Recurrence turns the submit into a SCHEDULE rather than a dispatch:
  // a repeating task is a rule the server replays, so there is nothing
  // to send to RMF today. A one-shot future start stays a plain
  // dispatch with `earliest_start_ms`, which the command path already
  // handles and which does not need a rule to describe it.
  const [repeats, setRepeats] = useState(false);
  const [recurrence, setRecurrence] = useState<RecurrenceSpec>(() =>
    defaultRecurrenceSpec(new Date()),
  );
  const [scheduleName, setScheduleName] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Sync prefill changes (calendar slot click). When the calendar
  // hands us a slot, default the schedule toggle to on so the user
  // sees the time they actually clicked.
  //
  // And when a prefill is TAKEN AWAY -- the drawer's form stays mounted,
  // so the Month's "New task on this day" and then a plain "+ New task"
  // are one form -- the day it filled in goes with it: the plain one
  // opened still booked for that day, its button "Add to Calendar", for
  // an operator about to send a robot now (review, 2026-10-31). Only what
  // the prefill set is undone; the rest of a half-filled form stays. That
  // includes Advanced: closed again when the prefill opened it, left open
  // when the operator had (gate, 2026-10-31: the plain one opened with
  // Advanced showing an unticked "Schedule for later"), and left open when
  // they set a priority or a note in it, which stay and go out with the
  // task (review, 2026-10-31: closing hid them).
  const hadPrefill = useRef(false);
  const advancedByPrefill = useRef(false);
  const advancedOpenNow = useRef(advancedOpen);
  advancedOpenNow.current = advancedOpen;
  const valuesNow = useRef(values);
  valuesNow.current = values;
  useEffect(() => {
    if (prefillStart) {
      hadPrefill.current = true;
      if (!advancedOpenNow.current) advancedByPrefill.current = true;
      setScheduled(true);
      setAdvancedOpen(true);
      setValues((v) => ({
        ...v,
        startTime: localIsoForInput(prefillStart),
      }));
      setRecurrence(defaultRecurrenceSpec(prefillStart));
    } else if (hadPrefill.current) {
      hadPrefill.current = false;
      const v = valuesNow.current;
      const typedIn =
        (v.priority ?? DEFAULT_CATEGORY_FORM_VALUES.priority) !==
          DEFAULT_CATEGORY_FORM_VALUES.priority ||
        (v.notes ?? '') !== DEFAULT_CATEGORY_FORM_VALUES.notes;
      if (advancedByPrefill.current && !typedIn) setAdvancedOpen(false);
      advancedByPrefill.current = false;
      setScheduled(false);
      setRepeats(false);
      setValues((v) => ({ ...v, startTime: DEFAULT_CATEGORY_FORM_VALUES.startTime }));
      setRecurrence(defaultRecurrenceSpec(new Date()));
    }
  }, [prefillStart]);

  // Both write paths surface through the same error area, because from
  // the operator's side they are one action ("make this task happen")
  // and two error boxes for one button is two places to look.
  const submitError: unknown =
    (dispatch.isError ? dispatch.error : null) ??
    (createScheduleM.isError ? createScheduleM.error : null);

  useEffect(() => {
    if (!submitError) {
      setFieldErrors({});
      setFormError(null);
      return;
    }
    // A fire-mode refusal is a HOLD, not a denial: the request was well
    // formed and the operator has the capability. Rendering it with the
    // generic failure copy sends them hunting for a permission problem
    // that does not exist.
    if (isFireModeError(submitError)) {
      setFieldErrors({});
      setFormError(FIRE_MODE_HOLD_COPY);
      return;
    }
    // The same distinction one refusal later. A 409
    // `maintenance_window_active` is also a HOLD: the window lifts by
    // itself at a time somebody already chose, and the operator keeps
    // the capability throughout. Reaching here at all means a window
    // opened (or was extended) after this form loaded — the gate below
    // refuses one it can see — so this path also refreshes what the
    // form knows; see the refetch effect next to the query.
    if (isMaintenanceHold(submitError)) {
      setFieldErrors({});
      setFormError(MAINTENANCE_HOLD_COPY);
      return;
    }
    // High from someone who may not set it. The choice is locked for them,
    // so this is a role changed since the console loaded, or a stale
    // console. Said at the choice it is about, in the api-server's own
    // words, with Advanced opened so it is seen.
    if (isHighPriorityRefusal(submitError)) {
      setFieldErrors({ priority: submitError.message });
      setFormError(null);
      setAdvancedOpen(true);
      return;
    }
    const err = submitError as DispatchError;
    if (err?.status === 422) {
      const { fieldErrors: fe, formError: feMsg } = extractPydanticErrors(err.body);
      const map: Record<string, string> = {};
      for (const { field, message } of fe) map[field] = message;
      setFieldErrors(map);
      setFormError(feMsg);
      return;
    }
    setFieldErrors({});
    setFormError(err?.message ?? 'Dispatch failed');
  }, [submitError]);

  // A fire-mode refusal is true only while the alarm stands. When it
  // clears, drop that refusal so the form stops saying "frozen" until
  // the next press, as the shell's hold notice does. Cleared on the
  // active -> clear TRANSITION only: a refusal can arrive before the
  // alarm itself is known here. Read without `useFireAlarm`, which
  // throws outside its provider, because this form also renders alone.
  const fireAlarmActive = useContext(FireAlarmContext)?.isFireAlarmActive ?? false;
  const wasFireAlarmActive = useRef(fireAlarmActive);
  useEffect(() => {
    if (wasFireAlarmActive.current && !fireAlarmActive) {
      if (dispatch.isError && isFireModeError(dispatch.error)) dispatch.reset();
      if (createScheduleM.isError && isFireModeError(createScheduleM.error)) createScheduleM.reset();
    }
    wasFireAlarmActive.current = fireAlarmActive;
    // Only the transition matters; the mutations are stable objects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fireAlarmActive]);

  // The projection this comment used to promise now exists, so the
  // categories come from the site rather than from four literals in
  // this file. `rmf/v1/sites/{site}/task_catalog` is the worker's
  // intersection of: what the site's fleet adapters accept, what the
  // worker can build a request for, and what the building map has
  // places for.
  const catalog = catalogQuery.data ?? [];

  // Keep the selection inside what the site actually offers.
  //
  // Only moves when the current category is NOT offered, so it never
  // fights an operator's choice; and it depends on the joined category
  // list rather than the array identity, so a refetch returning an
  // equal catalog does not reset a half-filled form.
  // Operands for the category in hand, straight from the catalog. The
  // zones are load-bearing rather than cosmetic — see the zone field.
  const cleanEntry = catalog.find((e) => e.category === 'clean');
  const cleanZones = cleanEntry?.zones ?? [];
  /** The modes this site offers, from the catalog (vacuum only when it
   *  says nothing) — the same list the worker checks a dispatch against,
   *  so nothing offered here is refused there. */
  const cleanModes = offeredCleanModes(cleanEntry);
  /** The selected zone's estimated clean time, whole minutes (at least
   *  1), from the catalog's per-zone table; null when it has none. */
  const zoneEstimateS = values.zone ? cleanEntry?.zone_estimates_s?.[values.zone] : undefined;
  const zoneEstimateMin =
    typeof zoneEstimateS === 'number' && zoneEstimateS > 0
      ? Math.max(1, Math.round(zoneEstimateS / 60))
      : null;
  /** The site's DustBagStations, from the catalog and NOWHERE ELSE.
   *
   *  They arrive in the ordinary `places` operand list, but they are
   *  not the whole place list: a DustBagStation is a plain holding
   *  point with no distinguishing map param, so the worker takes the
   *  names the SITE declared (`NEXSOSS_DUSTBAG_STATIONS`) and
   *  intersects them with the live nav graph. Naming
   *  `L04-DustbagStation-1`/`-2` here instead would have been shorter
   *  and would break the moment a second site with different stations
   *  came up -- and worse, it would offer a station a typo had already
   *  dropped from the real list, dispatching a task to a place no
   *  fleet can reach. */
  const dustbagEntry = catalog.find((e) => e.category === 'dustbag');
  const dustbagStations = dustbagEntry?.places ?? [];
  /** What an empty wait field means at THIS site, read from the same
   *  catalog entry the worker builds the dispatch from — so the number
   *  staff are shown is the number the robot keeps. */
  const siteDustbagWait = siteDustbagWaitMinutes(dustbagEntry?.dustbag_timeout_s);
  /** The site's charger places, from the catalog and NOWHERE ELSE —
   *  the same route the dustbag stations take, for the same reasons.
   *  A charger is a plain named waypoint with no distinguishing map
   *  param in the v1 contract, so the worker takes the names the SITE
   *  declared (`NEXSOSS_CHARGER_PLACES`) and intersects them with the
   *  live nav graph; an option here is a place a fleet can actually
   *  reach today. */
  const chargerPlaces = catalog.find((e) => e.category === 'charge')?.places ?? [];
  /** What each priority level does at this site's robots, as the worker
   *  publishes it; null until a worker that acts on priority runs, and
   *  undefined while the catalog is loading or could not be read -- read
   *  from the query, not `catalog`, whose `?? []` would say "none". */
  const effects = priorityEffects(catalogQuery.data);
  const canSetHigh = useCanSetHighPriority();

  /** Fleets that can actually reach the selected zone.
   *
   *  Two fleets serving the same zone both bid, and RMF picks on cost --
   *  distance and battery -- not on which one you meant. hotel2 is exactly
   *  that case: the simulated `cleanerBotA` and the real `phantas_fleet`
   *  register `clean` on the same nav graph and serve the same three zones,
   *  so a Clean dispatched with no target goes to whichever happens to be
   *  closer. `fleet_name` on the request suppresses every other fleet's bid.
   *
   *  Sourced from the place's own `fleets` (the worker builds it from
   *  /nav_graphs, which only RUNNING adapters publish) rather than from a
   *  fleet list, so this offers only fleets that can reach THIS zone and
   *  are up right now. See nexsoss-deploy/docs/cleaning-zones.md section 5. */
  const zoneFleets = useMemo(() => {
    if (!values.zone) return [];
    const place = (placesQuery.data ?? []).find((p) => p.name === values.zone);
    return place?.fleets ?? [];
  }, [values.zone, placesQuery.data]);
  const offered = catalog.map((e) => fromV1Category(e.category)).join(',');
  useEffect(() => {
    if (!offered) return; // empty or not loaded — leave the seed alone
    const ids = offered.split(',');
    if (!ids.includes(category)) setCategory(ids[0] as Category);
  }, [offered]); // eslint-disable-line react-hooks/exhaustive-deps
  // ...and the clean mode inside what the site offers, by the same rule.
  const offeredModes = cleanModes.join(',');
  useEffect(() => {
    if (!cleanModes.includes(cleanMode)) setCleanMode(cleanModes[0] ?? DEFAULT_CLEAN_MODE);
  }, [offeredModes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parent-driven prefill (Overview map: click a zone → Clean).
  //
  // The category is already seeded in `useState` above, but the seed
  // alone is not enough to make the form self-sufficient against the
  // reconciliation effect it sits next to: this effect RE-ASSERTS the
  // prefill once — keyed on the catalog's arrival — and is declared
  // AFTER the reconciliation effect, so within the commit where
  // `offered` first becomes non-empty the order is reconcile-then-
  // prefill and an offered prefill always wins. A parent may therefore
  // mount the form before `useTaskCatalog` has data without the
  // prefill being clobbered.
  //
  // The zone can only be applied here, never in the seed: the zone
  // field is a <select> over `catalog.zones`, and a value has to be
  // checked against that list before it is allowed into state — an
  // unknown zone left in state would be silently submittable (the
  // rmf_demos executor "cleans" it by reporting COMPLETED). Unknown →
  // the select stays on its placeholder and the submit stays disabled.
  //
  // Once per mount (the ref latch), so a later catalog refetch — or
  // this effect re-running for any reason — can never fight edits the
  // operator has made since. A prefill the site does not offer is left
  // to the reconciliation effect above, which moves the category onto
  // an offered one; nothing unknown is ever submitted.
  const prefillApplied = useRef(false);
  useEffect(() => {
    if (prefillApplied.current) return;
    if (!prefillCategory && !prefillZone) return;
    if (!offered) return; // catalog not here yet — keep the latch unspent
    prefillApplied.current = true;
    if (prefillCategory && offered.split(',').includes(prefillCategory)) {
      setCategory(prefillCategory);
    }
    if (prefillZone && cleanZones.includes(prefillZone)) {
      setValues((v) => ({ ...v, zone: prefillZone }));
    }
  }, [offered, prefillCategory, prefillZone]); // eslint-disable-line react-hooks/exhaustive-deps

  function setField<K extends keyof CategoryFormValues>(key: K, val: CategoryFormValues[K]) {
    setValues((v) => ({ ...v, [key]: val }));
  }

  function categoryRequiredComplete(): boolean {
    if (category === 'station') return Boolean(values.destination?.trim());
    if (category === 'patrol') return Boolean(values.waypoints?.trim());
    if (category === 'clean') return Boolean(values.zone?.trim());
    if (category === 'dustbag') return Boolean(values.dustbagStation?.trim());
    if (category === 'charge') return Boolean(values.chargePlace?.trim());
    return false;
  }

  /**
   * A scheduled start has to be filled in and resolvable — and, for a
   * ONE-SHOT, still ahead of the site's clock.
   *
   * The past check is not tidiness. The scheduler dispatches an
   * occurrence up to `GRACE_MS` late and records anything older as
   * `missed` WITHOUT dispatching it (`backend/api_server/scheduler.py`),
   * so a start time already gone produces a calendar entry that silently
   * never runs — and until now the only validation was "non-empty".
   * `NEAR_TERM_MS` IS that grace, so the boundary here is the server's
   * own: everything this accepts, the server still runs.
   *
   * It applies to a one-shot ONLY. For a recurrence the start time is
   * the rule's ANCHOR (its `dtstart`), not its next run: "every Monday
   * 09:00" anchored on last Monday is a rule the server accepts and
   * expands from the NEXT Monday, which is exactly how an operator
   * writes "the usual Monday clean, starting from the Monday it has
   * always been on". Blocking it refuses a legitimate schedule, and the
   * past-time warning would be describing a task that is not in the
   * past at all. A past anchor is surfaced as an advisory instead —
   * `anchorIsPast()` below.
   *
   * Judged on `dtstartMs` — the typed digits read on the SITE's clock —
   * because that is the instant the server will act on.
   */
  function scheduleValid(): boolean {
    if (!scheduled) return true;
    if (!values.startTime) return false;
    // Unparseable digits: no anchor to store on either route.
    if (dtstartMs === null || Number.isNaN(dtstartMs)) return false;
    if (recurring) return true;
    return dtstartMs > Date.now() - NEAR_TERM_MS;
  }

  /** True when the operator has typed a start the site's clock has
   *  already passed, whatever the form then does about it. */
  function startTimeIsPast(): boolean {
    return (
      scheduled &&
      Boolean(values.startTime) &&
      dtstartMs !== null &&
      dtstartMs <= Date.now() - NEAR_TERM_MS
    );
  }

  /** The blocking case: a ONE-SHOT whose moment is gone. The one reason
   *  `scheduleValid` fails that an operator cannot see by looking at
   *  the field, so it is the one that gets the warning. */
  function startTimeBlocksSubmit(): boolean {
    return startTimeIsPast() && !recurring;
  }

  /** The advisory case: a RECURRENCE anchored before now. Legal, stored
   *  as written, and expanded from the next matching occurrence — worth
   *  saying out loud (an operator who mistyped the year should notice)
   *  but never a reason to refuse the rule. */
  function anchorIsPast(): boolean {
    return startTimeIsPast() && recurring;
  }

  /** Comma-separated names (a single place, or "patrol"'s list) that
   *  aren't in the real building's waypoint registry. Empty (no
   *  errors) whenever the registry hasn't loaded / has nothing named
   *  — see `placesLoaded` above for why that degrades open. */
  function unknownPlaces(raw: string): string[] {
    if (!placesLoaded) return [];
    const names = raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return names.filter((n) => !knownPlaceNames.has(n));
  }

  function clientFieldErrors(): Record<string, string> {
    const errors: Record<string, string> = {};
    if (category === 'station' && values.destination?.trim()) {
      const bad = unknownPlaces(values.destination);
      if (bad.length) errors.destination = `Unknown place: ${bad.join(', ')}`;
    }
    if (category === 'patrol' && values.waypoints?.trim()) {
      const bad = unknownPlaces(values.waypoints);
      if (bad.length) errors.waypoints = `Unknown place(s): ${bad.join(', ')}`;
    }
    if (
      category === 'dustbag' &&
      parseDustbagWaitMinutes(values.dustbagWaitMinutes) === 'invalid'
    ) {
      errors.dustbagWaitMinutes = `Enter whole minutes from ${DUSTBAG_WAIT_MIN_MINUTES} to ${DUSTBAG_WAIT_MAX_MINUTES}, or leave it empty.`;
    }
    return errors;
  }

  const clientErrors = clientFieldErrors();
  const busy = dispatch.isPending || createScheduleM.isPending;
  /** The rule's anchor. A recurrence cannot be built without one, which
   *  is why the Repeat control only appears once a start time exists. */
  const dtstart = useMemo(() => {
    if (!scheduled || !values.startTime) return null;
    const d = new Date(values.startTime);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [scheduled, values.startTime]);
  /** The anchor as an INSTANT, read on the site's clock. Every decision
   *  below — hold or dispatch, and what the form says it will do — is
   *  made from this rather than from `dtstart.getTime()`, which reads
   *  the typed digits on the operator's own clock and is 8 hours out
   *  for a Singapore hotel booked from London. */
  const dtstartMs = useMemo(
    () => (dtstart ? dtstartMsInZone(dtstart, siteZone.timeZone) : null),
    [dtstart, siteZone.timeZone],
  );
  /** `repeats` is only meaningful with an anchor. Deriving the effective
   *  flag (rather than reading the raw checkbox) means a stale `repeats`
   *  can never gate the submit button on a control that is off screen —
   *  a missing start time is then reported by `scheduleValid`, which is
   *  the reason the operator can actually act on. */
  const recurring = repeats && dtstart !== null;
  const recurrenceValid =
    !recurring || recurrence.end !== 'on' || Boolean(recurrence.untilDate);
  /** A one-shot far enough out to be worth holding on the calendar.
   *
   *  Read on every render so the form's own description of what the
   *  button does tracks the time being typed. `submit` re-decides from
   *  a fresh clock rather than trusting this: a form left open across
   *  the threshold would otherwise promise one thing and do the other.
   *  Within a few seconds of the boundary the two can disagree, and the
   *  disagreement is harmless — both branches run the task at the
   *  requested time, and only the route differs. */
  const heldOneShot =
    !recurring && dtstartMs !== null && dtstartMs - Date.now() > NEAR_TERM_MS;
  /** Everything that becomes a calendar entry instead of an immediate
   *  hand-off to RMF. */
  const heldOnCalendar = recurring || heldOneShot;

  /**
   * A clock that ticks only while the scheduling section is open.
   *
   * Nothing reads its VALUE for arithmetic — the reads below take a
   * fresh `Date.now()` — it exists to make the component re-render
   * while the operator sits on an open form, so the chips' `past` flags
   * below are re-evaluated rather than frozen at whatever the clock said
   * when the section was opened. Stopped whenever the chips are off
   * screen — the schedule toggle off, or the whole Advanced panel
   * collapsed with it still on: a timer behind a closed panel is a
   * battery cost with no reader.
   */
  const [scheduleClock, setScheduleClock] = useState(() => Date.now());
  const scheduleSectionVisible = advancedOpen && scheduled;
  useEffect(() => {
    if (!scheduleSectionVisible) return;
    setScheduleClock(Date.now());
    const id = window.setInterval(() => setScheduleClock(Date.now()), SCHEDULE_CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, [scheduleSectionVisible]);

  /**
   * What each quick-start chip WOULD set, resolved on the SITE's
   * calendar.
   *
   * Recomputed whenever the scheduling section is opened or the zone
   * changes rather than on a timer: the values only have to be right at
   * the moment they are clicked, and a chip that rewrites itself under
   * the cursor every thirty seconds is worse than one that is a minute
   * stale.
   */
  const quickPickTargets = useMemo(() => {
    const zone = siteZone.timeZone;
    const nowMs = Date.now();
    const defs = [
      { id: 'tonight', label: 'Tonight 22:00', days: 0, hour: 22 },
      { id: 'tomorrow', label: 'Tomorrow 09:00', days: 1, hour: 9 },
      {
        id: 'next-monday',
        label: 'Next Monday 09:00',
        days: daysToNextMonday(zone, nowMs),
        hour: 9,
      },
    ] as const;
    return defs.map((d) => {
      const value = quickPickValue(zone, d.days, d.hour, nowMs);
      const atMs = dtstartMsInZone(new Date(value), zone);
      return { id: d.id, label: d.label, value, atMs };
    });
    // `scheduled` is a dependency on purpose: reopening the section is
    // the moment the chips are about to be looked at, and the cheapest
    // honest place to refresh them.
  }, [siteZone.timeZone, scheduled]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * ...and whether each is still offerable, read from the clock AT
   * RENDER — deliberately outside the memo above.
   *
   * Frozen inside it, "Tonight 22:00" stayed enabled on a form opened at
   * 21:59 and clicked at 22:03, setting a start the server would record
   * `missed` and never dispatch. `scheduleClock` is what guarantees a
   * render happens at all while the form just sits there; `Date.now()`
   * is what makes the answer exact at the moment it is drawn.
   */
  const chipNowMs = Math.max(scheduleClock, Date.now());
  const quickPicks = quickPickTargets.map((q) => ({ ...q, past: q.atMs <= chipNowMs }));

  /** The instant a maintenance window would be judged against.
   *
   *  The server evaluates its gate WHEN THE REQUEST ARRIVES, not when
   *  the task runs (`_maintenance_blocks` in
   *  `backend/api_server/api/commands.py` asks about now), so the two
   *  routes out of this form ask about two different instants:
   *
   *    - held on the calendar → the request arrives at the occurrence's
   *      own start, so that is the instant to ask about. And it matters
   *      more here than anywhere: an occurrence that comes due inside a
   *      window is DROPPED, not deferred (`SKIPPED_MAINTENANCE`,
   *      `scheduler.py`) — the task simply never runs, and without this
   *      check nothing would have told the operator so.
   *    - dispatched now (immediate, or near-term enough that the form
   *      hands it straight to the fleet) → the request arrives NOW.
   *      `earliest_start_ms` is not gated, so asking about the start
   *      time on this route would refuse the wrong thing in both
   *      directions.
   *
   *  `dtstartMs`, never `dtstart.getTime()`: a window is a pair of
   *  instants and the typed digits are a wall clock on the BUILDING's
   *  clock. Reading them on the operator's own is the bug the comment
   *  on `dtstartMs` describes, and here it would clear the form to
   *  submit a start time the command path then refuses — 8 hours out
   *  for a Singapore hotel booked from London. */
  const maintenanceAtMs = heldOnCalendar && dtstartMs !== null ? dtstartMs : Date.now();
  const maintenanceWindows = useMaintenanceWindows(maintenanceLookupRange(maintenanceAtMs));
  // Pulled out because `refetch` is stable across renders and the query
  // RESULT object is not. Depending on the whole of it would re-run the
  // effect below on every refetch that effect itself caused, which is a
  // loop, not a refresh.
  const { refetch: refetchMaintenanceWindows } = maintenanceWindows;

  // A refusal means this list is out of date — a window opened, or was
  // extended, after the form loaded. Refetching turns the generic hold
  // copy the error path just set into the specific gate below ("until
  // 05:00 — lift service") and disables the button, instead of leaving
  // an operator to press it again for the same 409.
  useEffect(() => {
    if (isMaintenanceHold(submitError)) void refetchMaintenanceWindows();
  }, [submitError, refetchMaintenanceWindows]);

  /** The window that would refuse this dispatch, or undefined.
   *
   *  Advisory only — the server refuses on the command path whatever
   *  the browser believed, and that refusal is handled above. This
   *  exists so the operator finds out BEFORE filling in a form whose
   *  submit is guaranteed to 409. Missing data (still loading, request
   *  failed) reads as "not blocked": degrading open is the same choice
   *  `placesLoaded` makes, and the authoritative answer is one round
   *  trip away regardless.
   *
   *  A recurrence anchored in the PAST is the one case with no instant
   *  worth asking about: `dtstartMs` is then not a real first
   *  occurrence (the rule expands from a future one this form cannot
   *  compute) and `Date.now()` is not either (nothing is dispatched
   *  now). Asking anyway would refuse a perfectly good "every Monday
   *  09:00" because a maintenance window happened to cover last
   *  Monday's 09:00. Degrade open, as above. */
  const maintenanceHold = anchorIsPast()
    ? undefined
    : windowCovering(maintenanceWindows.data, maintenanceAtMs);

  /** Names the window and says when it lifts. "Held" with neither is a
   *  support ticket; with both it is something an operator can plan
   *  around. On the SITE's clock, like every other time this form
   *  prints. In the calendar's words (2026-10-31): "blocked time", and
   *  "remove" it, never "shorten" -- Remove is the only change the
   *  console offers a booked one. */
  const maintenanceHoldMessage = !maintenanceHold
    ? null
    : `${
        recurring
          ? `The first run falls in blocked time${maintenanceWindowName(maintenanceHold.reason)}, so it would be dropped rather than held over — later runs are unaffected.`
          : heldOnCalendar
            ? `That start time falls in blocked time${maintenanceWindowName(maintenanceHold.reason)}, so the task would be dropped rather than held over: it would never run.`
            : `This site is in blocked time${maintenanceWindowName(maintenanceHold.reason)}, so nothing is being dispatched right now.`
      } Dispatch resumes at ${formatZonedDateTime(maintenanceHold.ends_at_ms, siteZone.timeZone)} ${timeZoneLabel(siteZone.timeZone, maintenanceHold.ends_at_ms)}. Start it after that, or remove the blocked time on the calendar.`;

  /** After a 409 the refetch above usually lands within a second, and
   *  then the server's generic hold copy and the gate's own message say
   *  the same thing twice — except the gate's names the window and its
   *  end time. Two paragraphs about one hold read as two problems, so
   *  the specific one wins. */
  const holdCopySupersededByWindow =
    maintenanceHoldMessage !== null && formError === MAINTENANCE_HOLD_COPY;

  // Viewing as another role is look-only: the form is shown as that role
  // sees it, but nothing is sent, so its send button is drawn disabled
  // rather than left to fail in the form's red error style.
  const actionsOff = useActionsOff();

  const canSubmit =
    !actionsOff &&
    categoryRequiredComplete() &&
    scheduleValid() &&
    recurrenceValid &&
    Object.keys(clientErrors).length === 0 &&
    !maintenanceHold &&
    !busy;

  /** Placeholder and blank-field fallback for the schedule's name. A
   *  schedule is addressed by name everywhere it appears later (the
   *  calendar block, the move/delete dialog), so an empty one is worse
   *  than a generated one. */
  function suggestedScheduleName(): string {
    const label =
      category === 'clean'
        ? CLEAN_MODE_LABEL[cleanMode]
        : category === 'dustbag'
          ? ROUTINE_CHECK_LABEL
          : (catalog.find((e) => fromV1Category(e.category) === category)?.label ?? category);
    const target =
      values.zone || values.destination || values.chargePlace || values.waypoints || '';
    return target ? `${label} — ${target}` : label;
  }

  /** The category/phases/priority/schema_ref fields shared by a real
   *  dispatch and a saved favourite — a favourite is nothing more than
   *  these same fields with a name attached, minus any one-off
   *  scheduling (a saved template re-runs "now" each time, not at a
   *  fixed clock time from when it was saved). Returns `null` when the
   *  category's required field(s) aren't filled in (mirrors the
   *  submit button's own disabled condition). */
  function buildDispatchFields(): DispatchFields | null {
    return dispatchFieldsFromForm(category, values, cleanMode);
  }

  function resetAfterSubmit() {
    setValues(DEFAULT_CATEGORY_FORM_VALUES);
    setScheduled(false);
    setRepeats(false);
    setScheduleName('');
    setAdvancedOpen(false);
    advancedByPrefill.current = false;
    onSuccess?.();
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const fields = buildDispatchFields();
    if (!fields) return; // shouldn't happen — button is disabled
    // Suppresses every other fleet's bid (RMF task_request.fleet_name).
    // Omitted entirely when unset -- an explicit null would be a
    // different request than "no preference".
    const task = { ...fields, ...(values.fleet ? { fleet: values.fleet } : {}) };

    // Decided here, from a clock read at the moment of the click, for
    // both the recurring and the one-shot case. `heldOneShot` is a
    // render-time value and a form can sit open for an hour.
    const holdOneShot =
      !recurring && dtstartMs !== null && dtstartMs - Date.now() > NEAR_TERM_MS;

    if ((recurring && dtstart) || holdOneShot) {
      createScheduleM.mutate(
        {
          name: scheduleName.trim() || suggestedScheduleName(),
          // Deliberately WITHOUT `earliest_start_ms`: the stored payload
          // is replayed at every occurrence, so a start time baked into
          // it would pin all of them to the first one.
          task,
          // A one-shot is stored as a schedule with a single occurrence
          // — same table, same expansion, same dispatch path — so it is
          // held on the calendar until its moment instead of sitting in
          // a robot's queue from the day it was booked, and it can be
          // moved by dragging it like anything else on the calendar.
          rrule: recurring && dtstart ? buildRRule(recurrence, dtstart) : ONE_SHOT_RRULE,
          // The typed digits are a wall clock, and the clock they belong
          // to is the SITE's. `dtstart.getTime()` read them as the
          // operator's own: a London engineer entering "daily 09:00" for
          // a Singapore hotel wrote a 16:00 clean that then moved itself
          // to 17:00 when the UK fell back, at a site with no DST.
          dtstart_ms: dtstartMs!,
          // Recurrence is wall-clock — a 09:00 clean stays at 09:00
          // across a DST shift — so the server needs the zone the rule
          // was written in. That is the site's zone, from its `sites`
          // row, NOT `Intl.DateTimeFormat().resolvedOptions().timeZone`.
          timezone: siteZone.timeZone,
          // Never carried by a one-shot. `recurrence` keeps whatever the
          // editor was last left holding, so an operator who opened the
          // repeat controls, set "ends on 1 Sep" and then switched
          // repeat back off would otherwise store a single occurrence
          // with an end bound — and if that bound sits before the start,
          // the server refuses the rule as one that can never fire.
          until_ms: recurring ? buildUntilMs(recurrence, siteZone.timeZone) : null,
          enabled: true,
          // No `color`. A schedule is drawn in its TYPE's colour,
          // always — see the lane paint (`calendar/lane-style.ts`) for
          // why the per-schedule picker went. The column and the v1
          // field still exist and are still nullable; this simply never
          // writes them.
        },
        { onSuccess: resetAfterSubmit },
      );
      return;
    }

    // Immediate, or near enough that holding it would risk the
    // scheduler recording it `missed` instead of running it. RMF gets
    // the task now and starts it at `earliest_start_ms` — read on the
    // SITE's clock, the same instant the hold/dispatch decision above
    // was made from. It used to be `dtstart.getTime()`, the operator's
    // own clock, so the two disagreed by the offset between them.
    dispatch.mutate(
      {
        task: { ...task, ...(dtstartMs !== null ? { earliest_start_ms: dtstartMs } : {}) },
        requestedVia: prefillStart ? 'calendar' : 'dashboard',
      },
      { onSuccess: resetAfterSubmit },
    );
  }

  const createFavorite = useCreateFavoriteV1();
  const [favoriteOpen, setFavoriteOpen] = useState(false);
  const [favoriteName, setFavoriteName] = useState('');
  const [favoriteShared, setFavoriteShared] = useState(false);
  const canSaveFavorite = categoryRequiredComplete() && Object.keys(clientErrors).length === 0;

  function saveFavorite() {
    // Checked again here, not only on the toggle that opens the panel:
    // the form can turn invalid while the panel is open, and
    // `buildPhases` drops a wait it cannot read rather than sending it,
    // so a favourite saved then would quietly run on the site default.
    if (!canSaveFavorite) return;
    const fields = buildDispatchFields();
    if (!fields || !favoriteName.trim()) return;
    createFavorite.mutate(
      { name: favoriteName.trim(), is_shared: favoriteShared, ...fields },
      {
        onSuccess: () => {
          setFavoriteOpen(false);
          setFavoriteName('');
          setFavoriteShared(false);
        },
      },
    );
  }

  return (
    <form className="nx-task-form" onSubmit={submit} data-testid="task-dispatch-form">
      <fieldset className="nx-task-form__fieldset" disabled={busy}>
        {/* `site` is nullable (`useActiveSite`) — the screens that mount
            this form guard on it, but the type does not. */}
        <legend>Dispatch a new task{site ? ` — ${site.label}` : ''}</legend>

        <label className="nx-task-form__row">
          <span>Category</span>
          <select
            value={category === 'clean' ? cleanPickerValue(cleanMode) : category}
            onChange={(e) => {
              const picked = parsePickerValue(e.target.value);
              setCategory(picked.category);
              if (picked.mode) setCleanMode(picked.mode);
            }}
            data-testid="task-category-select"
          >
            {catalog.flatMap((entry) =>
              entry.category === 'clean'
                ? // One choice per mode the site offers: staff pick
                  // "Scrub", not "Clean" and then a mode.
                  cleanModes.map((mode) => (
                    <option key={`clean:${mode}`} value={cleanPickerValue(mode)}>
                      {CLEAN_MODE_LABEL[mode]}
                    </option>
                  ))
                : [
                    <option key={entry.category} value={fromV1Category(entry.category)}>
                      {/* A worker older than the rename still calls it
                          "Dustbag"; this console names it. */}
                      {entry.category === 'dustbag' ? ROUTINE_CHECK_LABEL : entry.label}
                    </option>,
                  ],
            )}
          </select>
          {/* Backstop for a category this site does not offer. The
              `<option>`s come from the catalog, so a person cannot pick
              one; code setting `category` (a prefill path, a stale
              favorite) still can, and this is the only thing that would
              explain the rejection.

              Tested against the CATALOG rather than a hardcoded list.
              This used to read `category !== 'patrol'` and claim patrol
              was the only category that really dispatched, which was
              false for station_docking and clean and told operators
              their working Clean task would be rejected. The catalog is
              already the right answer: the worker only emits a category
              that has a description builder AND satisfies its operand
              requirements (`build_catalog` in
              backend/worker/worker/task_catalog.py), which is the same
              table `rmf_commands.py` rejects against. So "in the
              catalog" and "will really dispatch" are the same fact, and
              hardcoding a second copy of it is what went stale. */}
          {!offered.split(',').includes(category) && (
            <span className="nx-task-form__field-error">
              This site does not offer {category} — submitting will be rejected.
            </span>
          )}
        </label>

        {category === 'station' && (
          <label className="nx-task-form__row">
            <span>Destination place</span>
            <input
              type="text"
              list={PLACES_DATALIST_ID}
              value={values.destination ?? ''}
              onChange={(e) => setField('destination', e.target.value)}
              placeholder={placesLoaded ? 'Start typing a place name…' : 'e.g. lobby'}
              required
              aria-invalid={
                fieldErrors['destination'] ||
                clientErrors['destination'] ||
                fieldErrors['phases'] ||
                fieldErrors['description']
                  ? true
                  : undefined
              }
              data-testid="task-destination"
            />
            {/* The one line of product copy this field gets, because
                "Station" answers a question nobody can answer from the
                word alone (it is not an RMF task type — the worker
                dispatches it as a one-place patrol). */}
            <span className="nx-task-form__hint">
              Station: send the robot to dock/park at one place.
            </span>
            {(fieldErrors['destination'] || clientErrors['destination']) && (
              <span className="nx-task-form__field-error" data-testid="task-error-destination">
                {fieldErrors['destination'] || clientErrors['destination']}
              </span>
            )}
          </label>
        )}

        {category === 'patrol' && (
          <label className="nx-task-form__row">
            <span>Waypoints (in visit order)</span>
            <WaypointMultiSelect
              value={values.waypoints ?? ''}
              onChange={(v) => setField('waypoints', v)}
              options={(placesQuery.data ?? []).map((p) => p.name)}
              placeholder={
                placesLoaded ? 'lobby, shop, lobby…' : 'Type a place name and press Enter'
              }
              testId="task-waypoints"
            />
            {(fieldErrors['waypoints'] || clientErrors['waypoints']) && (
              <span className="nx-task-form__field-error" data-testid="task-error-waypoints">
                {fieldErrors['waypoints'] || clientErrors['waypoints']}
              </span>
            )}
          </label>
        )}

        {category === 'clean' && (
          <label className="nx-task-form__row">
            <span>Zone / area</span>
            {/* A SELECT, not a text input with a datalist.
                `rmf_demos_fleet_adapter`'s clean executor answers an
                unknown zone by logging an error and calling
                `execution.finished()` -- the task reports COMPLETED
                having cleaned nothing, and no screen anywhere can tell
                that apart from a real clean. A datalist SUGGESTS its
                options and accepts anything typed, which is not enough
                here. The catalog's zones come from `is_cleaning_zone`
                vertices in the building map, so this offers exactly
                the zones that exist. */}
            <select
              value={values.zone ?? ''}
              onChange={(e) => {
                setField('zone', e.target.value);
                // A fleet chosen for the PREVIOUS zone may not serve
                // this one; keeping it would send a request no fleet
                // can bid on, which reads as "nothing happened".
                setField('fleet', undefined);
              }}
              required
              aria-invalid={fieldErrors['zone'] ? true : undefined}
              data-testid="task-zone"
            >
              <option value="" disabled>
                Select a zone…
              </option>
              {cleanZones.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
            {fieldErrors['zone'] && (
              <span className="nx-task-form__field-error" data-testid="task-error-zone">
                {fieldErrors['zone']}
              </span>
            )}
            {/* The site's own estimate for this zone (its lookup table,
                for robots whose API gives none) -- the number RMF plans
                the task and the battery with. Nothing when there is no
                table. */}
            {zoneEstimateMin !== null && (
              <span className="nx-task-form__hint" data-testid="task-zone-estimate">
                Est. {zoneEstimateMin} min
              </span>
            )}
            {/* The one thing the platform cannot check for a Scrub. The
                robot refuses it without the water tank, and the task
                then fails like any other clean, with the robot's
                reason — saying so here saves a walk to the robot.
                Dust-mop gets no hint (Akash: "offer it, no hint"). */}
            {cleanMode === 'scrub' && (
              <span className="nx-task-form__hint" data-testid="task-scrub-hint">
                Fit and fill the water tank first.
              </span>
            )}
          </label>
        )}

        {category === 'dustbag' && (
          <label className="nx-task-form__row">
            <span>Maintenance point</span>
            {/* A SELECT over the CATALOG's list, for the same reason
                the clean's zone is one, plus one of its own.

                The catalog's dustbag stations are the names the SITE
                declared (`NEXSOSS_DUSTBAG_STATIONS`) INTERSECTED with
                the live nav graph, so an option here is a place a
                fleet can actually reach today. Hardcoding
                `L04-DustbagStation-1`/`-2` would be shorter and would
                keep offering a station after a typo, a map republish
                or a second site had removed it -- and the worker's
                `_dustbag_description` requires exactly one real place,
                so the failure would land at dispatch with nothing on
                screen explaining it.

                An EMPTY list is a real answer, not a loading state:
                the worker omits the category entirely when the site
                declared no reachable station, so if this renders at
                all there is at least one option. */}
            <select
              value={values.dustbagStation ?? ''}
              onChange={(e) => setField('dustbagStation', e.target.value)}
              required
              aria-invalid={fieldErrors['dustbagStation'] ? true : undefined}
              data-testid="task-dustbag-station"
            >
              <option value="" disabled>
                Select a maintenance point…
              </option>
              {dustbagStations.map((station) => (
                <option key={station} value={station}>
                  {station}
                </option>
              ))}
            </select>
            <span className="nx-task-form__hint">
              The robot drives here and waits for a routine check: empty the dustbag and check the
              brushes. If nobody confirms in time, it finishes anyway and is flagged “not
              confirmed”. Confirm from the robot or the task once it is done.
            </span>
            {fieldErrors['dustbagStation'] && (
              <span className="nx-task-form__field-error" data-testid="task-error-dustbagStation">
                {fieldErrors['dustbagStation']}
              </span>
            )}
          </label>
        )}

        {category === 'dustbag' && (
          <label className="nx-task-form__row">
            <span>Wait for staff up to (minutes)</span>
            {/* OPTIONAL, and empty is the normal case. The site's usual
                wait is set once, for the site (`NEXSOSS_DUSTBAG_TIMEOUT_S`,
                published on the catalog), because a number retyped on
                every dispatch is a number that eventually gets typed
                wrong. This is for the exception — a busy shift, a bin
                room far away — and only a filled-in value is sent.

                TEXT with a numeric keypad, not `type="number"`. A number
                input hands React "" for anything the browser cannot
                parse ("45 min", "45.", a stray "."), so a typo read as
                an EMPTY field: no message, and Save as favourite (a
                plain button, which the browser's own check never runs
                for) stored the job with the site default instead of the
                wait typed. As text, every character reaches
                `parseDustbagWaitMinutes`, which names it back. */}
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={values.dustbagWaitMinutes ?? ''}
              onChange={(e) => setField('dustbagWaitMinutes', e.target.value)}
              placeholder={String(siteDustbagWait)}
              aria-invalid={clientErrors['dustbagWaitMinutes'] ? true : undefined}
              data-testid="task-dustbag-timeout"
            />
            <span className="nx-task-form__hint" data-testid="task-dustbag-timeout-hint">
              Leave empty to use this site&apos;s usual {siteDustbagWait} minutes.
            </span>
            {clientErrors['dustbagWaitMinutes'] && (
              <span
                className="nx-task-form__field-error"
                data-testid="task-error-dustbagWaitMinutes"
              >
                {clientErrors['dustbagWaitMinutes']}
              </span>
            )}
          </label>
        )}

        {category === 'charge' && (
          <label className="nx-task-form__row">
            <span>Charger place</span>
            {/* A SELECT over the CATALOG's list, for the same reasons
                the dustbag station is one.

                The catalog's charger places are the names the SITE
                declared (`NEXSOSS_CHARGER_PLACES`) INTERSECTED with
                the live nav graph, so an option here is a charger a
                fleet can actually reach today. Hardcoding
                `Phantas_charger` would be shorter and would keep
                offering it after a typo, a map republish or a second
                site had removed it — and the worker's charge builder
                requires exactly one real place, so the failure would
                land at dispatch with nothing on screen explaining it.
                A datalist SUGGESTS its options and accepts anything
                typed, which is not enough here.

                An EMPTY list is a real answer, not a loading state:
                the worker omits the category entirely when the site
                declared no reachable charger, so if this renders at
                all there is at least one option. */}
            <select
              value={values.chargePlace ?? ''}
              onChange={(e) => setField('chargePlace', e.target.value)}
              required
              aria-invalid={fieldErrors['chargePlace'] ? true : undefined}
              data-testid="task-charge-place"
            >
              <option value="" disabled>
                Select a charger…
              </option>
              {chargerPlaces.map((place) => (
                <option key={place} value={place}>
                  {place}
                </option>
              ))}
            </select>
            <span className="nx-task-form__hint">
              The robot drives to this charger and docks. This does not change its automatic
              charging setting — that lives on the robot, under Robots.
            </span>
            {fieldErrors['chargePlace'] && (
              <span className="nx-task-form__field-error" data-testid="task-error-chargePlace">
                {fieldErrors['chargePlace']}
              </span>
            )}
          </label>
        )}

        {/* Only shown when the zone is genuinely contended. With one
            fleet there is nothing to choose and a disabled-looking
            control invites the question "which should I pick?" for no
            reason; with none, the zone is unreachable and the dispatch
            will fail at auction regardless of what is selected here. */}
        {category === 'clean' && zoneFleets.length > 1 && (
          <label className="nx-task-form__row">
            <span>Fleet</span>
            <select
              value={values.fleet ?? ''}
              onChange={(e) => setField('fleet', e.target.value || undefined)}
              data-testid="task-fleet"
            >
              {/* Deliberately a real option rather than a blank: leaving
                  it unset is a legitimate choice (let RMF pick the
                  cheapest bid), and it should read as a choice rather
                  than as a field somebody forgot. */}
              <option value="">Any fleet (lowest bid wins)</option>
              {zoneFleets.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            {/* "The system", not "RMF": the front desk books here (Akash,
                2026-10-31: no "RMF" where the hotel sees). */}
            <span className="nx-task-form__hint" data-testid="task-fleet-hint">
              {zoneFleets.length} fleets serve this zone. Leave unset and the
              system picks on cost, which is distance and battery — not on which
              robot you had in mind.
            </span>
          </label>
        )}

        {/* Every place here is reachable by at least one RUNNING fleet:
            the worker builds this registry from `/nav_graphs`, which
            only running fleet adapters publish. It previously came
            from the building map, which lists every graph the map
            author defined — so the picker offered waypoints belonging
            to fleets that weren't running, and the dispatch failed
            later at auction ("waypoint name for Place [X] cannot be
            found in the navigation graph") rather than being
            preventable here. Showing the owning fleet makes it obvious
            which fleet would take the task, and why a place is absent
            when its fleet is down. */}
        <datalist id={PLACES_DATALIST_ID}>
          {(placesQuery.data ?? []).map((p) => (
            <option key={p.name} value={p.name}>
              {[p.level, p.fleets?.length ? p.fleets.join(', ') : null].filter(Boolean).join(' · ')}
            </option>
          ))}
        </datalist>

        {/* Three different states, said differently. The old copy --
            "No categories configured - falling back to defaults" --
            described a fallback that did not exist and covered all
            three at once. */}
        {catalogQuery.isLoading && (
          <span className="nx-task-form__hint">Loading task categories…</span>
        )}
        {catalogQuery.isError && (
          <span className="nx-task-form__field-error">
            Could not load this site&apos;s task categories, so none can be offered. This is not a
            statement about what the site supports.
          </span>
        )}
        {!catalogQuery.isLoading && !catalogQuery.isError && catalog.length === 0 && (
          <span className="nx-task-form__field-error">
            This site publishes no dispatchable task categories. A category is offered only when the
            site&apos;s fleet adapters accept it, the worker can build the request, and the building
            map has places for it — so this usually means the worker predates the task catalog, or
            the site declares nothing in its descriptor.
          </span>
        )}

        <section className="nx-task-form__advanced">
          <button
            type="button"
            className="nx-task-form__advanced-toggle"
            aria-expanded={advancedOpen}
            aria-controls="task-form-advanced-panel"
            onClick={() => {
              // Opened or closed by hand, it is the operator's to keep.
              advancedByPrefill.current = false;
              setAdvancedOpen((v) => !v);
            }}
            data-testid="task-advanced-toggle"
          >
            <span className="nx-task-form__advanced-chev" aria-hidden="true">
              {advancedOpen ? '▾' : '▸'}
            </span>
            Advanced
          </button>
          {advancedOpen && (
            <div
              id="task-form-advanced-panel"
              className="nx-task-form__advanced-panel"
              data-testid="task-advanced-panel"
            >
              <PriorityChoice
                value={priorityLevel(values.priority)}
                onChange={(level) => setField('priority', level)}
                canSetHigh={canSetHigh}
                effects={effects}
                error={fieldErrors['priority']}
                testId="task-priority"
              />
              <label className="nx-task-form__row">
                <span>Notes</span>
                <input
                  type="text"
                  value={values.notes ?? ''}
                  onChange={(e) => setField('notes', e.target.value)}
                  placeholder="Optional context for the operator on the floor"
                  data-testid="task-notes"
                />
              </label>
              <label className="nx-task-form__row nx-task-form__row--inline">
                <input
                  type="checkbox"
                  checked={scheduled}
                  onChange={(e) => {
                    setScheduled(e.target.checked);
                    // Recurrence is meaningless without a scheduled
                    // start, and its controls unmount with this section
                    // — leaving `repeats` on would wedge the submit
                    // button behind a checkbox that is no longer on
                    // screen to switch off.
                    if (!e.target.checked) setRepeats(false);
                  }}
                  data-testid="task-schedule-toggle"
                />
                <span>Schedule for later</span>
              </label>
              {scheduled && (
                <>
                  {/* The three starts operators actually ask for, so the
                      common case is one click instead of eight typed
                      digits. Computed on the SITE's calendar — "tomorrow"
                      for a Singapore hotel booked from London is not the
                      browser's tomorrow. A chip whose moment has already
                      gone is offered but refused rather than silently
                      producing a start the server would record `missed`. */}
                  <div
                    className="nx-task-form__quickpicks"
                    role="group"
                    aria-label="Quick start times"
                  >
                    {quickPicks.map((q) => (
                      <button
                        key={q.id}
                        type="button"
                        className="nx-task-form__quickpick"
                        disabled={q.past}
                        title={
                          q.past
                            ? `${q.label} has already passed at this site`
                            : `Start at ${formatZonedDateTime(q.atMs, siteZone.timeZone)} ${timeZoneLabel(siteZone.timeZone, q.atMs)}`
                        }
                        onClick={() => setField('startTime', q.value)}
                        data-testid={`task-quickpick-${q.id}`}
                      >
                        {q.label}
                      </button>
                    ))}
                  </div>
                  <DateTimeField
                    className="nx-task-form__row"
                    label="Start time"
                    ariaLabel="Start time"
                    value={values.startTime ?? ''}
                    onChange={(v) => setField('startTime', v)}
                    // Deliberately UNBOUNDED. A native `min` here was a
                    // second, disagreeing gate: it is a browser-local
                    // wall clock compared against digits this form reads
                    // on the SITE's clock, it refused the ~5-minute
                    // near-term window `scheduleValid()` accepts (so the
                    // button was enabled for a value the browser then
                    // rejected with its own bubble), it moved under a
                    // value the operator had already committed as the
                    // clock advanced, and it would refuse the past
                    // ANCHOR of a recurrence, which is legal. The form's
                    // own validation — `scheduleValid()` plus the
                    // warning below — is the one gate, and it is the one
                    // that can explain itself.
                    data-testid="task-start-time"
                  />
                  {/* The typed digits, read back as the instant they
                      actually name. Everything downstream — the hold
                      decision, the maintenance gate, the stored rule —
                      is made from this value, and it is the one an
                      operator in another zone cannot work out by
                      looking at the field. */}
                  {dtstartMs !== null && (
                    <span className="nx-task-form__hint" data-testid="task-start-time-echo">
                      Resolves to {formatZonedDateTime(dtstartMs, siteZone.timeZone)}{' '}
                      {timeZoneLabel(siteZone.timeZone, dtstartMs)}
                    </span>
                  )}
                  {startTimeBlocksSubmit() && (
                    <span
                      className="nx-task-form__hint nx-task-form__hint--warn"
                      role="alert"
                      data-testid="task-start-time-past"
                    >
                      That time has already passed at this site. A schedule that comes due
                      late is recorded as missed and never dispatched — pick a time still
                      ahead of {timeZoneLabel(siteZone.timeZone)}.
                    </span>
                  )}
                  {/* A recurrence's start time is where the pattern is
                      MEASURED FROM, not when it next runs, so a past one
                      is legal and common ("the usual Monday clean").
                      Said out loud anyway, without the word "missed" and
                      without blocking anything, so an operator who meant
                      to type next year still notices. */}
                  {anchorIsPast() && (
                    <span className="nx-task-form__hint" data-testid="task-start-time-anchor-past">
                      That time has already passed at this site. It is used as the
                      repeat pattern's starting point, so the schedule runs from the next
                      occurrence of the pattern summarised below, not from this one.
                    </span>
                  )}
                </>
              )}
              {scheduled && (
                <label className="nx-task-form__row nx-task-form__row--inline">
                  <input
                    type="checkbox"
                    checked={repeats}
                    // Only block TURNING IT ON without an anchor. A box
                    // that is checked must always stay operable, or
                    // clearing the start time traps the form in a state
                    // the operator cannot leave.
                    disabled={dtstart === null && !repeats}
                    onChange={(e) => {
                      setRepeats(e.target.checked);
                      // Seed the rule from the start time the user just
                      // picked, so the weekday chips open on the day
                      // they actually chose rather than on today.
                      if (e.target.checked && dtstart) setRecurrence(defaultRecurrenceSpec(dtstart));
                    }}
                    data-testid="task-repeat-toggle"
                  />
                  <span>Repeat{dtstart === null ? ' (pick a start time first)' : ''}</span>
                </label>
              )}
              {/* Named because it is now a thing on a calendar that
                  somebody has to recognise later — in the grid, in the
                  move/delete dialog, in the schedule list. A one-shot
                  gets the same field for the same reason; leaving it
                  blank takes the suggestion. */}
              {heldOnCalendar && (
                <label className="nx-task-form__row">
                  <span>{recurring ? 'Schedule name' : 'Calendar entry name'}</span>
                  <input
                    type="text"
                    value={scheduleName}
                    onChange={(e) => setScheduleName(e.target.value)}
                    placeholder={suggestedScheduleName()}
                    data-testid="task-schedule-name"
                  />
                </label>
              )}
              {scheduled && repeats && dtstart && (
                <RecurrenceEditor
                  spec={recurrence}
                  onChange={setRecurrence}
                  dtstart={dtstart}
                  timeZone={siteZone.timeZone}
                />
              )}
              {/* The two routes a scheduled task can take differ in ways
                  an operator can see on the floor — whether a robot is
                  holding the job from now, whether the task appears in
                  the live list, whether it can still be moved — so the
                  form says which one this is instead of letting them
                  find out. "Move to" rather than dragging (2026-10-31):
                  the panel's Move to works in every view, the drag only
                  in Day. */}
              {scheduled && dtstartMs !== null && (
                <span className="nx-task-form__hint" data-testid="task-schedule-hint">
                  {heldOnCalendar
                    ? recurring
                      ? 'Held on the calendar. Each run is dispatched at its time — nothing is sent to the fleet now.'
                      : `Held on the calendar until ${formatZonedDateTime(dtstartMs, siteZone.timeZone)} ${timeZoneLabel(siteZone.timeZone, dtstartMs)}, then dispatched. Nothing is sent to the fleet now, and you can still move it: select it on the calendar and use Move to.`
                    : `Starting within ${NEAR_TERM_MS / 60000} minutes, so this goes to the fleet now and starts at ${formatZonedDateTime(dtstartMs, siteZone.timeZone)} ${timeZoneLabel(siteZone.timeZone, dtstartMs)}. A robot holds it from now, and a dispatched task cannot be rescheduled — cancel and re-book it to change the time.`}
                </span>
              )}
              {scheduled && dtstart && zoneWarning && (
                <span
                  className="nx-task-form__hint nx-task-form__hint--warn"
                  role="alert"
                  data-testid="task-schedule-zone-warning"
                >
                  {zoneWarning}
                </span>
              )}
            </div>
          )}
        </section>

        {/* Pinned above any field's open dropdown (see WaypointMultiSelect's
         * z-index) so the primary action is never covered — previously a
         * suggestion popup could overlay this button, silently swallowing
         * the first click as a dismiss instead of a submit. */}
        <div className="nx-task-form__footer">
          <div className="nx-task-form__footer-row">
            <button
              type="submit"
              className="nx-task-form__submit"
              disabled={!canSubmit}
              title={actionsOff ? 'Actions are off while viewing as another role' : undefined}
              data-testid="task-dispatch-submit"
            >
              {busy
                ? heldOnCalendar
                  ? 'Saving…'
                  : 'Dispatching…'
                : recurring
                  ? 'Create Schedule'
                  : heldOneShot
                    ? 'Add to Calendar'
                    : 'Dispatch Task'}
            </button>
            <button
              type="button"
              className="nx-task-form__save-favorite-toggle"
              disabled={!canSaveFavorite}
              aria-expanded={favoriteOpen}
              onClick={() => setFavoriteOpen((v) => !v)}
              data-testid="task-save-favorite-toggle"
            >
              <span aria-hidden="true">☆</span> Save as favourite
            </button>
          </div>
          {/* In the FOOTER, not in the Advanced panel where the start
              time lives: that panel is closed by default, and the
              commonest case this refuses — an immediate dispatch during
              an open window — never opens it. A disabled button with no
              visible reason is the bug this is here to prevent. */}
          {maintenanceHoldMessage && (
            <span className="nx-task-form__error" role="alert" data-testid="task-maintenance-hold">
              {maintenanceHoldMessage}
            </span>
          )}
          {formError && !holdCopySupersededByWindow && (
            <span className="nx-task-form__error" role="alert" data-testid="task-form-error">
              {formError}
            </span>
          )}
          {submitError !== null && !formError && (
            <span className="nx-task-form__error" role="alert">
              {String((submitError as Error)?.message ?? 'Dispatch failed')}
            </span>
          )}
          {favoriteOpen && (
            <div
              className="nx-task-form__save-favorite-panel"
              data-testid="task-save-favorite-panel"
            >
              <label className="nx-task-form__row">
                <span>Favourite name</span>
                <input
                  type="text"
                  value={favoriteName}
                  onChange={(e) => setFavoriteName(e.target.value)}
                  placeholder="e.g. Evening lobby patrol"
                  autoFocus
                  data-testid="task-favorite-name"
                />
              </label>
              <label className="nx-task-form__row nx-task-form__row--inline">
                <input
                  type="checkbox"
                  checked={favoriteShared}
                  onChange={(e) => setFavoriteShared(e.target.checked)}
                  data-testid="task-favorite-shared"
                />
                <span>Share with everyone on this site</span>
              </label>
              {/* A favourite is run by whoever presses Run, and High is a
                  supervisor's call each time: anyone else's run goes as
                  Normal (`task_priority.py`). Said before it is saved. */}
              {isHighPriority(values.priority) && (
                <span className="nx-task-form__hint" data-testid="task-favorite-high-note">
                  Saved as High. It runs as High when a supervisor runs it; anyone else&apos;s run
                  goes as Normal.
                </span>
              )}
              <div className="nx-task-form__save-favorite-actions">
                <button
                  type="button"
                  className="nx-task-form__save-favorite-cancel"
                  onClick={() => {
                    setFavoriteOpen(false);
                    setFavoriteName('');
                    setFavoriteShared(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="nx-task-form__save-favorite-confirm"
                  disabled={!canSaveFavorite || !favoriteName.trim() || createFavorite.isPending}
                  onClick={saveFavorite}
                  data-testid="task-favorite-save"
                >
                  {createFavorite.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
              {createFavorite.isError && (
                <span className="nx-task-form__error" role="alert">
                  {String((createFavorite.error as Error)?.message ?? 'Could not save favourite')}
                </span>
              )}
            </div>
          )}
        </div>
      </fieldset>
    </form>
  );
}
